import React, { createContext, useContext, useState, useEffect } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal, useAccount } from '@azure/msal-react';
import { fetchAzureConfig, createMsalConfig } from '../config/authConfig';
import './AuthProvider.css';

// Create Auth Context
const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
    const [msalInstance, setMsalInstance] = useState(null);
    const [configError, setConfigError] = useState(null);
    const [isConfigLoading, setIsConfigLoading] = useState(true);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                setIsConfigLoading(true);
                setConfigError(null);
                
                // Fetch Azure AD configuration from backend
                const azureConfig = await fetchAzureConfig();
                
                // Create MSAL configuration
                const msalConfig = createMsalConfig(azureConfig);
                
                // Create MSAL instance
                const instance = new PublicClientApplication(msalConfig);
                
                // Initialize MSAL
                await instance.initialize();
                
                setMsalInstance(instance);
                console.log('MSAL initialized successfully');
                
            } catch (error) {
                console.error('Failed to initialize MSAL:', error);
                setConfigError(error.message || 'Authentication configuration failed');
            } finally {
                setIsConfigLoading(false);
            }
        };

        initializeMsal();
    }, []);

    // Show loading state while initializing
    if (isConfigLoading) {
        return (
            <div className="auth-loading">
                <div className="auth-loading-content">
                    <div className="auth-spinner"></div>
                    <p>Initializing authentication...</p>
                </div>
            </div>
        );
    }

    // Show error state if configuration failed
    if (configError) {
        return (
            <div className="auth-error">
                <div className="auth-error-content">
                    <h2>Authentication Setup Error</h2>
                    <p>{configError}</p>
                    <button onClick={() => window.location.reload()}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Render with MSAL provider once initialized
    if (msalInstance) {
        return (
            <MsalProvider instance={msalInstance}>
                <AuthContextProvider>
                    {children}
                </AuthContextProvider>
            </MsalProvider>
        );
    }

    return null;
};

// Internal Auth Context Provider
const AuthContextProvider = ({ children }) => {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [accessToken, setAccessToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Get access token for API calls
    const getAccessToken = async () => {
        if (!account) return null;

        try {
            setIsLoading(true);
            const response = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
            });
            setAccessToken(response.accessToken);
            return response.accessToken;
        } catch (error) {
            console.error('Token acquisition failed:', error);
            // If silent token acquisition fails, try interactive
            try {
                const response = await instance.acquireTokenPopup({
                    scopes: ["User.Read"],
                    account: account,
                });
                setAccessToken(response.accessToken);
                return response.accessToken;
            } catch (interactiveError) {
                console.error('Interactive token acquisition failed:', interactiveError);
                return null;
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Login function
    const login = async () => {
        try {
            setIsLoading(true);
            const loginResponse = await instance.loginPopup({
                scopes: ["User.Read"],
                prompt: "select_account"
            });
            
            if (loginResponse) {
                const token = await getAccessToken();
                return token;
            }
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Logout function
    const logout = async () => {
        try {
            setIsLoading(true);
            setAccessToken(null);
            await instance.logoutPopup();
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Get user info
    const getUserInfo = () => {
        if (!account) return null;
        
        return {
            name: account.name,
            username: account.username,
            email: account.username,
            displayName: account.name || account.username,
            isAuthenticated: true
        };
    };

    // Check if user is authenticated
    const isAuthenticated = !!account;

    // Auto-acquire token when account changes
    useEffect(() => {
        if (account && !accessToken) {
            getAccessToken();
        }
    }, [account, accessToken]);

    const value = {
        isAuthenticated,
        user: getUserInfo(),
        accessToken,
        isLoading,
        login,
        logout,
        getAccessToken,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;